import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Bell, CalendarClock, Check, ChevronDown, ChevronRight, Loader2, Lock, Mail, MessageCircle, MessageSquareText, RotateCcw, Save, ShieldAlert, Table2, X } from 'lucide-react';
import { api } from '@/api/client';
import { useAuth } from '@/hooks/useAuth';

interface Channel { key: string; label: string; hint: string; value: string }
interface MisColumn { key: string; label: string; group: string; sensitive?: boolean }
interface MisRecipient { email: string | null; ok: boolean; error: string | null; at: string }
/** One mailing: the whole recipient list for a single sheet, not one row per address. */
interface MisSend {
  startedAt: string;
  sentAt: string;
  subject: string | null;
  fileName: string | null;
  rows: number;
  sentBy: string;
  scheduled: boolean;
  sent: number;
  failed: number;
  recipients: MisRecipient[];
}
interface MisHistory { days: number; since: string; sends: MisSend[]; emails: number; truncated: boolean }
interface Mis {
  enabled: boolean;
  /** null = this merchant has never been customised, so the platform default set applies. */
  columns: string[] | null;
  defaultColumns: string[];
  catalogue: MisColumn[];
  lastSentAt: string | null;
  lastStatus: string | null;
  lastRowCount: number | null;
  lastError: string | null;
  history?: MisHistory;
  historyDays?: number;
}
/** One schedulable report and whether it is armed for this merchant. */
interface ReportToggle {
  key: string;
  flag: string;
  label: string;
  hint: string;
  /** False when no scheduled job exists yet — the tick is stored but nothing acts on it. */
  hasSender: boolean;
  enabled: boolean;
  lastSentAt: string | null;
  lastStatus: string | null;
}

/**
 * What each stamped status means, in the words an operator would use.
 *
 * Two of these are NOT failures and must not read as one: a merchant with nothing outstanding gets
 * no mail on purpose, and one that is armed but has no address is a setup gap, not an error.
 */
const SENT_LABEL: Record<string, { text: string; cls: string }> = {
  SENT:           { text: 'sent',                     cls: 'text-emerald-700 bg-emerald-50 border-emerald-200' },
  PARTIAL:        { text: 'sent, some addresses failed', cls: 'text-amber-700 bg-amber-50 border-amber-200' },
  BLOCKED_EMPTY:  { text: 'nothing outstanding — not sent', cls: 'text-slate-500 bg-slate-50 border-slate-200' },
  NO_RECIPIENTS:  { text: 'no address on this report', cls: 'text-amber-700 bg-amber-50 border-amber-200' },
  FAILED:         { text: 'failed',                   cls: 'text-rose-700 bg-rose-50 border-rose-200' },
};

type AlertChannel = 'sms' | 'whatsapp';
type AlertValues = Record<string, Record<AlertChannel, boolean>>;
/** SMS / WhatsApp to the merchant's customers, per event. `live` = actually sends today. */
interface CustomerAlerts {
  events: { key: string; label: string; hint: string; live: Record<AlertChannel, boolean> }[];
  values: AlertValues;
  editable: boolean;
}

interface Payload {
  merchantId: number;
  accountEmail: string;
  channels: Channel[];
  customerAlerts?: CustomerAlerts;
  /** Absent for a merchant caller — arming a report is an internal decision. */
  reports?: ReportToggle[];
  /** Absent for a merchant caller — the API does not disclose the MIS selection to them. */
  mis?: Mis;
  updatedAt: string | null;
}

/**
 * The last week of MIS mailings for this merchant.
 *
 * Grouped by mailing, not by address. One MIS goes to every address on the list as its own email —
 * so a single morning's send is a dozen log rows, and listing them flat would read as a dozen
 * separate mailings and bury the question somebody opened this to answer: did today's sheet go, and
 * did every address on the list get it. Each row expands to the addresses when that is the question.
 *
 * A mailing where some addresses failed is called out in amber rather than being folded into a
 * "sent" tick — a bounce on one address is exactly the case where nobody notices for a month.
 */
function MisHistoryBlock({ history }: { history: MisHistory }) {
  const [open, setOpen] = useState<string | null>(null);

  const when = (iso: string) =>
    new Date(iso).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' });

  if (!history.sends.length) {
    return (
      <div className="px-3 py-3 text-xs text-slate-400 border-t border-slate-100">
        No MIS emails sent to this merchant in the last {history.days} days.
      </div>
    );
  }

  return (
    <div className="border-t border-slate-100">
      <div className="px-3 pt-2.5 pb-1.5 flex flex-wrap items-baseline gap-2">
        <span className="text-xs font-semibold text-slate-700 flex items-center gap-1.5">
          <CalendarClock size={13} className="text-slate-400" /> Sent in the last {history.days} days
        </span>
        <span className="text-xs text-slate-400">
          {history.sends.length} mailing{history.sends.length === 1 ? '' : 's'} · {history.emails} email
          {history.emails === 1 ? '' : 's'}
        </span>
      </div>

      <div className="divide-y divide-slate-100">
        {history.sends.map((sd) => {
          const id = sd.sentAt + (sd.fileName ?? '');
          const isOpen = open === id;
          return (
            <div key={id}>
              <button
                type="button"
                onClick={() => setOpen(isOpen ? null : id)}
                className="w-full px-3 py-2 flex items-center gap-2 text-left hover:bg-slate-50"
              >
                {isOpen ? <ChevronDown size={13} className="text-slate-400 shrink-0" />
                        : <ChevronRight size={13} className="text-slate-400 shrink-0" />}
                <span className="text-xs font-medium text-slate-700 shrink-0">{when(sd.sentAt)}</span>
                <span
                  className={`text-[10px] px-1.5 py-px rounded shrink-0 ${
                    sd.scheduled
                      ? 'bg-indigo-50 text-indigo-700 border border-indigo-200'
                      : 'bg-slate-100 text-slate-600 border border-slate-200'
                  }`}
                >
                  {sd.scheduled ? 'scheduled' : sd.sentBy}
                </span>
                <span className="text-xs text-slate-500 truncate">
                  {sd.rows.toLocaleString('en-IN')} rows · {sd.sent + sd.failed} recipient
                  {sd.sent + sd.failed === 1 ? '' : 's'}
                </span>
                <span className="ml-auto shrink-0 text-xs">
                  {sd.failed === 0 ? (
                    <span className="text-emerald-600 flex items-center gap-1"><Check size={12} /> all delivered</span>
                  ) : (
                    <span className="text-amber-600 flex items-center gap-1">
                      <ShieldAlert size={12} /> {sd.failed} of {sd.sent + sd.failed} failed
                    </span>
                  )}
                </span>
              </button>

              {isOpen && (
                <div className="px-3 pb-2.5 pl-8 space-y-1">
                  {sd.subject && <div className="text-[11px] text-slate-500 truncate">Subject: {sd.subject}</div>}
                  {sd.fileName && <div className="text-[11px] text-slate-400 truncate">File: {sd.fileName}</div>}
                  <div className="grid sm:grid-cols-2 gap-x-4 gap-y-0.5 pt-1">
                    {sd.recipients.map((r, i) => (
                      <div key={`${r.email}-${i}`} className="flex items-start gap-1.5 text-[11px]">
                        {r.ok ? <Check size={11} className="text-emerald-500 shrink-0 mt-0.5" />
                              : <X size={11} className="text-rose-500 shrink-0 mt-0.5" />}
                        <span className={r.ok ? 'text-slate-600' : 'text-rose-600'}>
                          {r.email ?? '—'}
                          {!r.ok && r.error && <span className="text-slate-400"> — {r.error}</span>}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {history.truncated && (
        <div className="px-3 pb-2 text-[11px] text-slate-400">
          Showing the most recent 500 emails — see Master Data → Logs for the rest.
        </div>
      )}
    </div>
  );
}

/**
 * Per-merchant notification routing — one comma-separated address list per mail stream.
 *
 * ONE component for both sides: the merchant edits it under Settings, an admin edits the same thing
 * under Merchant Master. A second implementation would drift, and then the merchant and the admin
 * would be looking at different truths about where their invoices go.
 *
 * `merchantId` is supplied by the admin screen; the merchant portal omits it and hits its own
 * id-less route.
 *
 * The MIS block below is the one part that is NOT shared: see `internal`.
 */
export function NotificationEmailsPanel({ merchantId, readOnly }: { merchantId?: number; readOnly?: boolean }) {
  const qc = useQueryClient();
  const { user } = useAuth();
  const base = merchantId ? `/merchants/${merchantId}/notification-emails` : '/merchant/notification-emails';
  const [draft, setDraft] = useState<Record<string, string>>({});
  // One place holds every report switch, MIS included, so the tick beside a report and the MIS
  // block below it can never disagree about whether that report is armed.
  const [flags, setFlags] = useState<Record<string, boolean>>({});
  const setFlag = (flag: string, on: boolean) => { setFlags((f) => ({ ...f, [flag]: on })); setDirty(true); };
  const misEnabled = flags.misEnabled === true;
  const setMisEnabled = (on: boolean) => setFlag('misEnabled', on);
  const [misColumns, setMisColumns] = useState<string[] | null>(null);
  const [alerts, setAlerts] = useState<AlertValues>({});
  const [dirty, setDirty] = useState(false);
  const setAlert = (event: string, channel: AlertChannel, on: boolean) => {
    setAlerts((a) => ({ ...a, [event]: { ...(a[event] ?? { sms: false, whatsapp: false }), [channel]: on } }));
    setDirty(true);
  };

  /**
   * Internal staff only.
   *
   * The whole reason the picker exists is to keep certain columns AWAY from a given client — if the
   * merchant could open this and tick "Charged Weight" back on, the control would be decorative.
   * The API refuses these two fields from a merchant token as well; this only stops us showing a
   * merchant a switch they are not allowed to throw.
   */
  const internal = user?.kind !== 'merchant';

  const { data, isLoading } = useQuery<Payload>({
    queryKey: ['merchant-notification-emails', merchantId ?? 'me'],
    queryFn: async () => (await api.get(base)).data,
  });

  // Seed the boxes once the server answers, but never overwrite something half-typed.
  useEffect(() => {
    if (!data || dirty) return;
    setDraft(Object.fromEntries(data.channels.map((c) => [c.key, c.value ?? ''])));
    setFlags(Object.fromEntries((data.reports ?? []).map((r) => [r.flag, r.enabled])));
    setMisColumns(data.mis?.columns ?? null);
    setAlerts(data.customerAlerts?.values ?? {});
  }, [data, dirty]);

  /**
   * At most three addresses per report, checked as it is typed rather than only on save.
   *
   * Every address is its own send with its own copy of the attachment, so the limit is about blast
   * size, not storage. A merchant who needs more should point one of these at a group address on
   * their own side. The server enforces the same number - this is here so nobody types out six
   * addresses and only finds out when the save is refused.
   */
  const MAX_EMAILS = 5;
  const countEmails = (v: string) => String(v ?? '').split(/[,;\s]+/).map((x) => x.trim()).filter(Boolean).length;
  const overLimit = (data?.channels ?? []).filter((c: any) => countEmails(draft[c.key] ?? '') > MAX_EMAILS);

  const save = useMutation({
    mutationFn: async () => {
      // `misColumns: []` is how "use the platform default set" is sent — the API turns an empty
      // array back into NULL. It is NOT "send an empty sheet", which is unsendable anyway.
      const body: any = { ...draft };
      if (internal) {
        Object.assign(body, flags);
        body.misColumns = misColumns ?? [];
        if (data?.customerAlerts?.editable) body.customerAlerts = alerts;
      }
      return (await api.put(base, body)).data;
    },
    onSuccess: () => {
      toast.success('Notification settings saved');
      setDirty(false);
      qc.invalidateQueries({ queryKey: ['merchant-notification-emails', merchantId ?? 'me'] });
    },
    onError: (e: any) => toast.error(e?.response?.data?.message ?? 'Could not save'),
  });

  if (isLoading) return <div className="py-10 text-center text-slate-400"><Loader2 size={22} className="animate-spin mx-auto" /></div>;

  const mis = data?.mis;
  const catalogue = mis?.catalogue ?? [];
  // No selection stored = the default set is what actually goes out, so that is what must be ticked.
  const selected = new Set(misColumns ?? mis?.defaultColumns ?? []);
  const usingDefaults = misColumns === null;
  const groups = Array.from(new Set(catalogue.map((c) => c.group)));

  const toggleCol = (key: string) => {
    if (readOnly) return;
    const next = new Set(selected);
    if (next.has(key)) next.delete(key); else next.add(key);
    // Keep catalogue order rather than click order — this list becomes the sheet's column order.
    setMisColumns(catalogue.filter((c) => next.has(c.key)).map((c) => c.key));
    setDirty(true);
  };

  /**
   * An empty selection is REFUSED rather than saved.
   *
   * The API reads `misColumns: []` as "this merchant has no custom set, use the platform default" —
   * which is what the Reset button relies on. So a save with everything unticked would hand the
   * merchant the FULL default sheet, including the columns just unticked to keep them away from it.
   * Blocking the save is the only reading that cannot surprise anyone: an empty sheet is not a
   * thing anyone wants sent, and silently-becomes-everything certainly is not.
   */
  const emptySelection = internal && !usingDefaults && selected.size === 0;

  const lastSent = mis?.lastSentAt
    ? new Date(mis.lastSentAt).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' })
    : null;

  return (
    <div className="space-y-4">
      <div className="flex items-start gap-2 text-sm text-slate-600 bg-slate-50 border border-slate-200 rounded-lg p-3">
        <Bell size={16} className="shrink-0 mt-0.5 text-slate-400" />
        <div>
          Choose who receives each kind of email. Separate addresses with commas — up to 5 per report.
          <div className="text-xs text-slate-500 mt-1">
            <b>One</b> email is sent per report, however many addresses you list: the <b>first address</b> goes in
            To and the other four in Cc, so everyone sees the same message rather than getting separate copies.
            <br />
            Leave a box empty and that mail goes to the account address,{' '}
            <b className="text-slate-700">{data?.accountEmail}</b>.
          </div>
        </div>
      </div>

      {/* SMS / WhatsApp to this merchant's CUSTOMERS, per event. Set by Gaint (sent in our name, at
          our cost); the merchant sees the choice read-only. A switch whose message is not connected
          yet is kept and says so, rather than pretending to send. */}
      {data?.customerAlerts && (() => {
        const ca = data.customerAlerts!;
        const canEdit = ca.editable && !readOnly;
        const Toggle = ({ event, channel }: { event: string; channel: AlertChannel }) => {
          const on = alerts[event]?.[channel] === true;
          return (
            <button type="button" role="switch" aria-checked={on} disabled={!canEdit}
              onClick={() => setAlert(event, channel, !on)}
              title={canEdit ? (on ? 'On — click to switch off' : 'Off — click to switch on') : 'Set by Gaint — contact your account manager to change'}
              className={`relative inline-flex h-5 w-9 shrink-0 items-center rounded-full transition ${on ? 'bg-emerald-500' : 'bg-slate-300'} ${canEdit ? 'cursor-pointer' : 'cursor-not-allowed opacity-70'}`}>
              <span className={`inline-block h-4 w-4 rounded-full bg-white shadow transition ${on ? 'translate-x-[18px]' : 'translate-x-0.5'}`} />
            </button>
          );
        };
        return (
          <div className="rounded-lg border border-slate-200 bg-white">
            <div className="flex flex-wrap items-center gap-2 px-3 py-2.5 border-b border-slate-100">
              <MessageSquareText size={15} className="text-slate-400" />
              <span className="text-sm font-semibold text-slate-700">Customer SMS &amp; WhatsApp</span>
              <span className="text-xs text-slate-400">messages to this merchant's customers (the consignee)</span>
              {!ca.editable && (
                <span className="ml-auto text-[11px] px-1.5 py-0.5 rounded bg-slate-100 text-slate-500 flex items-center gap-1">
                  <Lock size={10} /> set by Gaint — contact your account manager to change
                </span>
              )}
            </div>
            <table className="w-full text-sm">
              <thead className="text-[11px] uppercase text-slate-500 bg-slate-50">
                <tr>
                  <th className="text-left px-3 py-2">Event</th>
                  <th className="text-center px-3 py-2 w-40"><span className="inline-flex items-center gap-1"><MessageSquareText size={12} /> Send SMS</span></th>
                </tr>
              </thead>
              <tbody>
                {ca.events.map((e) => (
                  <tr key={e.key} className="border-t border-slate-100">
                    <td className="px-3 py-2">
                      <div className="font-medium text-slate-800">{e.label}</div>
                      <div className="text-[11px] text-slate-400">{e.hint}</div>
                    </td>
                    {(['sms'] as AlertChannel[]).map((ch) => (
                      <td key={ch} className="px-3 py-2 text-center">
                        <div className="flex flex-col items-center gap-0.5">
                          <Toggle event={e.key} channel={ch} />
                          {e.live[ch]
                            ? <span className="text-[10px] text-emerald-700">{alerts[e.key]?.[ch] ? 'sending' : 'not sending'}</span>
                            : <span className="text-[10px] text-slate-400" title="The switch is saved; nothing goes out until this message is connected">saved · not connected yet</span>}
                        </div>
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
            {/* WhatsApp has its own screen now (templates, price, history), so its old switches here
                — saved but never connected — are no longer shown. */}
            <div className="px-3 py-2 border-t border-slate-100 text-[11px] text-slate-500 flex items-center gap-1">
              <MessageCircle size={12} /> WhatsApp messages to customers: {internal ? 'see this merchant\'s WhatsApp tab' : 'see Settings → SMS & WhatsApp'}.
            </div>
          </div>
        );
      })()}

      <div className="space-y-3">
        {(data?.channels ?? []).map((c) => (
          <div key={c.key}>
            {(() => {
              const rep = (data?.reports ?? []).find((r) => r.key === c.key);
              // Support Tickets has no tick: that mail fires when someone replies to a ticket, so
              // there is no schedule to arm. Merchants see no ticks at all — arming a report is
              // ours to decide, and the API refuses these fields from a merchant token anyway.
              const showTick = internal && !!rep;
              return (
                <label className="flex items-baseline gap-2 mb-1 flex-wrap">
                  {showTick && (
                    <input
                      type="checkbox"
                      checked={flags[rep!.flag] === true}
                      disabled={readOnly}
                      onChange={(e) => setFlag(rep!.flag, e.target.checked)}
                      title={`Email ${rep!.label} to this merchant on a schedule`}
                      className="w-4 h-4 accent-primary self-center"
                    />
                  )}
                  <span className="text-sm font-semibold text-slate-700">{c.label}</span>
                  <span className="text-xs text-slate-400">{c.hint}</span>
                  {showTick && !rep!.hasSender && flags[rep!.flag] === true && (
                    <span className="text-[11px] px-1.5 py-0.5 rounded bg-amber-50 text-amber-700 border border-amber-200">
                      armed — the scheduler for this report is not built yet, so nothing sends
                    </span>
                  )}
                  {/* The last run for THIS report, beside the report — the same question the MIS
                      block below answers, asked of every scheduled report. */}
                  {showTick && rep!.hasSender && (rep!.lastSentAt ? (
                    <span className="text-[11px] text-slate-400 flex items-center gap-1.5">
                      last run {new Date(rep!.lastSentAt).toLocaleString('en-IN')}
                      {rep!.lastStatus && (
                        <span className={`px-1.5 py-0.5 rounded border ${SENT_LABEL[rep!.lastStatus]?.cls ?? 'text-slate-500 bg-slate-50 border-slate-200'}`}>
                          {SENT_LABEL[rep!.lastStatus]?.text ?? rep!.lastStatus}
                        </span>
                      )}
                    </span>
                  ) : flags[rep!.flag] === true ? (
                    <span className="text-[11px] text-slate-400">armed — has not run yet</span>
                  ) : null)}
                </label>
              );
            })()}
            <div className="relative">
              <Mail size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
              <input
                value={draft[c.key] ?? ''}
                readOnly={readOnly}
                onChange={(e) => { setDraft({ ...draft, [c.key]: e.target.value }); setDirty(true); }}
                placeholder={`${data?.accountEmail ?? 'accounts@example.com'} — first goes in To, rest in Cc (max 5)`}
                className={`input w-full pl-8 ${readOnly ? 'bg-slate-50 text-slate-500' : ''} ${countEmails(draft[c.key] ?? '') > MAX_EMAILS ? 'border-rose-400 focus:border-rose-500' : ''}`}
              />
            </div>
            {countEmails(draft[c.key] ?? '') > MAX_EMAILS && (
              <p className="mt-1 text-xs text-rose-600">
                {countEmails(draft[c.key] ?? '')} addresses — at most {MAX_EMAILS} can receive this report.
                Remove {countEmails(draft[c.key] ?? '') - MAX_EMAILS}, or use one group address that forwards to the others.
              </p>
            )}

            {/* The scheduled-MIS controls belong to the MIS Summary address, so they sit under it. */}
            {c.key === 'misSummary' && internal && mis && (
              <div className="mt-2 rounded-lg border border-slate-200 bg-white">
                <div className="flex flex-wrap items-center gap-3 px-3 py-2.5 border-b border-slate-100">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={misEnabled}
                      disabled={readOnly}
                      onChange={(e) => { setMisEnabled(e.target.checked); setDirty(true); }}
                      className="w-4 h-4 accent-primary"
                    />
                    <span className="text-sm font-semibold text-slate-700">Send the scheduled MIS to this merchant</span>
                  </label>
                  <span className="text-[11px] px-1.5 py-0.5 rounded bg-slate-100 text-slate-500 flex items-center gap-1">
                    <Lock size={10} /> internal only — the merchant cannot change this
                  </span>
                  <div className="ml-auto text-xs text-slate-500">
                    {lastSent ? (
                      <>
                        Last sent <b className="text-slate-700">{lastSent}</b>
                        {mis.lastRowCount != null && <> · {mis.lastRowCount} rows</>}
                        {mis.lastStatus && mis.lastStatus !== 'SENT' && (
                          <span className="ml-1 text-amber-600">({mis.lastStatus})</span>
                        )}
                      </>
                    ) : (
                      <span className="text-slate-400">Never sent</span>
                    )}
                  </div>
                </div>

                {mis.lastError && (
                  <div className="px-3 py-2 text-xs text-rose-700 bg-rose-50 border-b border-rose-100 flex items-start gap-1.5">
                    <ShieldAlert size={13} className="shrink-0 mt-0.5" /> Last attempt failed: {mis.lastError}
                  </div>
                )}

                <div className="px-3 py-2.5">
                  <div className="flex flex-wrap items-center gap-2 mb-2">
                    <span className="text-xs font-semibold text-slate-700 flex items-center gap-1.5">
                      <Table2 size={13} className="text-slate-400" /> Columns in this merchant's sheet
                    </span>
                    <span className="text-xs text-slate-400">
                      {selected.size} of {catalogue.length}
                      {usingDefaults && ' · platform default'}
                    </span>
                    {!readOnly && !usingDefaults && (
                      <button
                        type="button"
                        onClick={() => { setMisColumns(null); setDirty(true); }}
                        className="text-xs text-slate-500 hover:text-primary flex items-center gap-1"
                      >
                        <RotateCcw size={11} /> Reset to default
                      </button>
                    )}
                  </div>

                  <p className="text-[11px] text-slate-500 mb-2.5">
                    Untick a column and it is left out of every sheet this merchant receives. Marked
                    columns are the ones normally withheld — a charged weight on a sheet we sent is
                    something a client can argue the freight over.
                  </p>

                  <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-x-4 gap-y-3">
                    {groups.map((g) => (
                      <div key={g}>
                        <div className="text-[10px] font-bold uppercase tracking-wide text-slate-400 mb-1">{g}</div>
                        <div className="space-y-1">
                          {catalogue.filter((c) => c.group === g).map((col) => (
                            <label
                              key={col.key}
                              className={`flex items-start gap-1.5 text-xs ${readOnly ? '' : 'cursor-pointer'} ${
                                selected.has(col.key) ? 'text-slate-700' : 'text-slate-400'
                              }`}
                            >
                              <input
                                type="checkbox"
                                checked={selected.has(col.key)}
                                disabled={readOnly}
                                onChange={() => toggleCol(col.key)}
                                className="w-3.5 h-3.5 mt-0.5 accent-primary shrink-0"
                              />
                              <span>
                                {col.label}
                                {col.sensitive && (
                                  <span className="ml-1 text-[9px] px-1 py-px rounded bg-amber-50 text-amber-700 border border-amber-200 align-middle">
                                    sensitive
                                  </span>
                                )}
                              </span>
                            </label>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>

                  {emptySelection && (
                    <div className="mt-2 text-xs text-rose-600">
                      Pick at least one column. Leaving them all unticked cannot be saved — use
                      <b> Reset to default</b> if you meant the standard set.
                    </div>
                  )}
                </div>

                {mis.history && <MisHistoryBlock history={mis.history} />}
              </div>
            )}
          </div>
        ))}
      </div>

      {!readOnly && (
        <div className="flex items-center gap-3 pt-1">
          <button onClick={() => save.mutate()} disabled={save.isPending || !dirty || emptySelection || overLimit.length > 0}
            className="px-5 py-2 rounded-lg bg-primary text-white text-sm font-medium hover:bg-primary-700 disabled:opacity-50 flex items-center gap-2">
            {save.isPending ? <Loader2 size={15} className="animate-spin" /> : <Save size={15} />} Save
          </button>
          {overLimit.length > 0 && (
            <span className="text-xs text-rose-600">
              {overLimit.map((c: any) => c.label).join(', ')}: more than {MAX_EMAILS} addresses
            </span>
          )}
          {dirty && overLimit.length === 0 && <span className="text-xs text-amber-600">Unsaved changes</span>}
          {data?.updatedAt && !dirty && (
            <span className="text-xs text-slate-400">
              Last updated {new Date(data.updatedAt).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' })}
            </span>
          )}
        </div>
      )}
    </div>
  );
}
